/**
 * Event capture is an abstraction to be a single point to call to send data to data stores
 */

import HyperDX from '@hyperdx/browser';
import { DateTime } from 'luxon';
import { getCurrentBatteryLevelIfAvailable } from '../battery';
import { hasFeatureEnabled } from '../feature';
import { getCurrentPositionIfAvailable } from '../geo';
import captureSpecificError from '../sentry';
import { getStore } from '../store';
import {
  captureEvent as captureAmplitudeEvent,
  identify as identifyWithAmplitude,
  initialize as initializeAmplitude,
} from './amplitude';
import {
  captureEvent as captureAnonymousEvent,
  identify as identifyWithAnonymousDataCapture,
  initialize as initializeDataCapture,
} from './anonymous-data-capture';
import {
  captureEvent as captureGTMEvent,
  identify as identifyWithGTM,
} from './google-tag-manager';
import initializeScheduledDataCapture from './scheduled';

/**
 * Get current member id
 * @return {number} Current member id
 */
const getMemberId = () => {
  const { currentMember } = getStore().getState().backendService;

  // User isn't logged in
  if (!currentMember) return null;

  return currentMember?.id;
};

/**
 * Retrieves the email of the current logged-in member.
 * @returns {string|null} The email of the current member, or null if the user is not logged in.
 */
const getMemberEmail = () => {
  const { currentMember } = getStore().getState().backendService;

  // User isn't logged in
  if (!currentMember) return null;

  return currentMember?.email;
};

const captureDebuggingEvent = (step: string) => {
  const eventCaptureDebugging = hasFeatureEnabled('event-capture-debugging');
  if (eventCaptureDebugging) {
    captureAmplitudeEvent({
      type: `app: event capture debugging ${step}`,
      payload: {},
      sendToAmplitude: true,
    });
  }
};

/**
 * Capture an event of data, this is an abstraction over the many target locations for data
 */
export const captureEventAsync = async ({
  type,
  payload,
  userProperties = undefined,
  customEvent = true,
  sendToAmplitude = true,
}: {
  type: string;
  payload: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userProperties?: any;
  customEvent?: boolean;
  sendToAmplitude?: boolean;
}) => {
  captureDebuggingEvent('start');

  const timestamp = DateTime.now().toUTC().toISO();
  captureDebuggingEvent('fetched timestamp');
  const currentPosition = await getCurrentPositionIfAvailable();
  captureDebuggingEvent('fetched position');

  const { batteryLevel, batteryIsCharging } =
    await getCurrentBatteryLevelIfAvailable();
  captureDebuggingEvent('fetched battery');

  const memberId = getMemberId();
  captureDebuggingEvent('fetched member id');
  const memberEmail = getMemberEmail();
  captureDebuggingEvent('fetched email');

  // Capture the event within Google Tag Manager, if the event isn't configured within GTM, it'll be ignored
  // Type is passed through as a `custom.event.${type}` so be sure to configure this :)
  const event = {
    type,
    payload: {
      ...payload,
      userProperties,
      memberId,
      timestamp,
      latitude: currentPosition.latitude,
      longitude: currentPosition.longitude,
      speed: currentPosition.speed,
      heading: currentPosition.heading,
      altitude: currentPosition.altitude,
      altitudeAccuracy: currentPosition.altitudeAccuracy,
      accuracy: currentPosition.accuracy,
      batteryLevel,
      batteryIsCharging,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      buildVersion: window?.DECKEE_CONFIG?.SHA,
    },
  };

  captureDebuggingEvent('capture anonymous');
  try {
    // Try to capture the event on the device, and sync it through the event-capture lambda to our anonymous data store
    await captureAnonymousEvent({
      ...event,
    });
  } catch (e) {
    // if there is an error in the capture gtm event, attempt to send an event to amplitude to indicate this. Make sure that this code doesn't block the
    // rest of event capture by not awaiting the async function.
    captureAmplitudeEvent({
      type: 'app: error capturing anonymous event',
      payload: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message: (e as any)?.message,
      },
      sendToAmplitude: true,
    });
  }

  captureDebuggingEvent('capture gtm');
  // Capture the event within Google Tag Manager, if the event isn't configured within GTM, it'll be ignored
  // Type is passed through as a `custom.event.${type}` so be sure to configure this :)
  try {
    captureGTMEvent({
      ...event,
      customEvent,
    });
  } catch (e) {
    // if there is an error in the capture gtm event, attempt to send an event to amplitude to indicate this. Make sure that this code doesn't block the
    // rest of event capture by not awaiting the async function.
    captureAmplitudeEvent({
      type: 'app: error capturing gtm event',
      payload: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message: (e as any)?.message,
      },
      sendToAmplitude: true,
    });
  }

  captureDebuggingEvent('capture amplitude');
  try {
    await captureAmplitudeEvent({
      ...event,
      sendToAmplitude,
    });
  } catch (e) {
    // if there is an error in the capture amplitude event, attempt to send another event to amplitude to indicate this. It MAY be successful because
    // I think the issue may be in updating user properties, not sending the initial event to amplitude. Make sure that this code doesn't block the
    // rest of event capture by not awaiting the async function.
    captureAmplitudeEvent({
      type: 'app: error capturing amplitude event',
      payload: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message: (e as any)?.message,
      },
      sendToAmplitude: true,
    });
  }

  captureDebuggingEvent('setting hyperdx');

  // Set global attributes for HyperDX
  HyperDX.setGlobalAttributes({
    userId: memberId,
    userEmail: memberEmail,
    // To get to the commit please replace this value
    // https://github.com/deckee-development/deckee-incubator/commit/<release>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    release: window?.DECKEE_CONFIG?.GITHUB_SHA,
  });
  captureDebuggingEvent('finish');
};

// Capture event without the ability to wait for a response. This has caused issues because getting the current position can
// take time, and we don't want to block the UI for this.
export const captureEvent = (...rest: Parameters<typeof captureEventAsync>) => {
  captureEventAsync(...rest).catch((e) => {
    captureSpecificError(
      e,
      'containers/app-main-client/src/services/event-capture/index.ts',
    );
  });
};

/**
 * This is used for GTM to identify unique users.
 *
 * Contains more than just `id` but the extra properties aren't typed.
 */
export const identify = (identity: {
  /** Unique user ID. */
  id: string;
}) => {
  const { id } = identity || {}; // Incase identity isn't provided
  if (id) {
    identifyWithAnonymousDataCapture();
    identifyWithAmplitude(identity);
    identifyWithGTM(id);
  } else {
    identifyWithAmplitude(null);
    // do this when a user logs out - this will mean any subsequent events are not attributed to the same user.
    identifyWithGTM(null);
  }
};

export const initialize = async () => {
  await initializeAmplitude();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (window?.DECKEE_CONFIG?.dataCapture?.enabled) {
    await initializeDataCapture();
    await initializeScheduledDataCapture();
  }
};
